import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Loader2, Plus } from 'lucide-react';
import { Breadcrumbs } from '@/components';
import { TicketList } from '@/components/TicketList';
import { useBrand } from '@/contexts/BrandContext';
import { getMyRequests, getErrorCode } from '@/services/api';
import { Button, ErrorState } from '@airclou/ui';
import type { RequestScope } from '@/types';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'pending', label: 'Awaiting my reply' },
  { key: 'solved', label: 'Solved' },
  { key: 'closed', label: 'Closed' },
] as const;

const SCOPE_TABS = [
  { key: 'my_requests' as RequestScope, label: 'My requests' },
  { key: 'ccd' as RequestScope, label: "CC'd" },
  { key: 'organization' as RequestScope, label: 'Organization' },
];

export function MyRequestsPage() {
  const { isAuthenticated, signOut } = useBrand();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get('status') || 'all';
  const scope = (searchParams.get('scope') || 'my_requests') as RequestScope;
  const page = parseInt(searchParams.get('page') || '1', 10);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['portal-requests', status, scope, page],
    queryFn: () => getMyRequests({ status, scope, page, limit: 20 }),
    enabled: isAuthenticated,
    staleTime: 30 * 1000,
  });

  const setStatus = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('status', newStatus);
    params.delete('page');
    setSearchParams(params);
  };

  const setScope = (newScope: RequestScope) => {
    const params = new URLSearchParams(searchParams);
    params.set('scope', newScope);
    params.delete('page');
    setSearchParams(params);
  };

  const setPage = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  // Not authenticated: show sign-in prompt
  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
        <div className="max-w-md mx-auto text-center py-16">
          <h1 className="text-2xl font-bold text-foreground mb-2">Sign in to view your requests</h1>
          <p className="text-muted-foreground mb-6">
            Sign in with your email to see tickets you've submitted.
          </p>
          <Link to={`/auth/login?return_to=${encodeURIComponent('/requests')}`}>
            <Button>Sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'My requests' },
        ]}
      />

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">My requests</h1>
        <Link to="/requests/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New request
          </Button>
        </Link>
      </div>

      {/* Scope tabs */}
      <div className="flex gap-1 mb-4 border-b">
        {SCOPE_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setScope(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              scope === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Status filter tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatus(tab.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
              status === tab.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <ErrorState
          description={
            error instanceof AxiosError && error.response?.status === 401
              ? 'Your session has expired. Please sign in again.'
              : 'Failed to load your requests.'
          }
          action={
            error instanceof AxiosError && error.response?.status === 401 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  signOut();
                  navigate(`/auth/login?return_to=${encodeURIComponent('/requests')}`);
                }}
              >
                Sign in
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Try again
              </Button>
            )
          }
        />
      )}

      {data && !isLoading && (
        <>
          <TicketList tickets={data.data} />

          {/* Pagination */}
          {data.meta.totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-muted-foreground">
                {data.meta.total} {data.meta.total === 1 ? 'request' : 'requests'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center text-sm text-muted-foreground px-2">
                  Page {page} of {data.meta.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= data.meta.totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
